import React from 'react'
import styled, { StyledProps, css } from 'styled-components'

type TabType = 'default' | 'warning'

type TabOption = {
  title: string
  value: string
  type?: TabType
}

type TabProps = {
  options: TabOption[]
  active: string
  onChange: (val: string) => void
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  font-style: normal;

  font-size: 16px;
  padding: 0 24px;
`

const pickColor = (props: StyledProps<{ type: TabType; isActive: boolean }>) => {
  const active = props.isActive ? 'true' : 'false'
  const borderBottom = {
    default: {
      true: `2px solid ${props.theme.color.primary600}`,
      false: 'none',
    },
    warning: {
      true: `2px solid ${props.theme.color.accentDestructive}`,
      false: 'none',
    },
  } as const
  const color = {
    default: {
      true: props.theme.color.primary600,
      false: props.theme.color.onPrimaryDark,
    },
    warning: {
      true: props.theme.color.accentDestructive,
      false: props.theme.color.accentDestructive,
    },
  } as const

  return css`
    color: ${color[props.type][active]};
    border-bottom: ${borderBottom[props.type][active]};
  `
}

const TabItemWrapper = styled.div.attrs({
  className: 'TabItemWrapper',
})<StyledProps<{ isActive: boolean; type: TabType }>>`
  font-weight: 500;
  line-height: 24px;

  padding: 12px 5px;
  margin-right: 14px;
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
  ${({ isActive }) => ({
    cursor: isActive ? 'default' : 'pointer',
  })}
  ${pickColor}
`

const TabItem: React.FC<{ isActive: boolean; title: string; type?: TabType; onClick: () => void }> = ({
  title,
  isActive,
  onClick,
  type = 'default',
}) => {
  return (
    <TabItemWrapper isActive={isActive} onClick={onClick} type={type}>
      {title}
    </TabItemWrapper>
  )
}

export const Tabs: React.FC<TabProps> = ({ options, active, onChange }) => {
  return (
    <Wrapper>
      {options.map(tab => (
        <TabItem
          key={tab.value}
          isActive={tab.value === active}
          title={tab.title}
          type={tab.type || 'default'}
          onClick={() => onChange(tab.value)}
        />
      ))}
    </Wrapper>
  )
}
